import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import "./SiteVisitExecutiveReport.css";

const SECTION_CONFIG = [
  {
    key: "exterior",
    label: "Exterior",
    questionKeys: [
      "exterior-sidewalk",
      "exterior-windows",
      "exterior-vehicles",
      "exterior-plate-brackets",
      "exterior-stickers",
      "exterior-promotional",
      "exterior-flags"
    ]
  },
  {
    key: "interior",
    label: "Interior",
    questionKeys: [
      "interior-welcome",
      "interior-no-clutter",
      "interior-music",
      "interior-floors"
    ]
  },
  {
    key: "showroom",
    label: "Showroom",
    questionKeys: [
      "showroom-appearance",
      "showroom-campaign",
      "showroom-brochures",
      "showroom-desks"
    ]
  },
  {
    key: "customer",
    label: "Customer Areas",
    questionKeys: [
      "customer-coffee-water",
      "customer-seating",
      "customer-magazines",
      "customer-charging",
      "customer-tv",
      "customer-bathroom"
    ]
  },
  {
    key: "staff",
    label: "Staff Areas",
    questionKeys: [
      "staff-lunch-room",
      "staff-core-values",
      "staff-ohc-minutes"
    ]
  },
  {
    key: "parts",
    label: "Parts & Service",
    questionKeys: [
      "parts-appearance",
      "parts-campaign",
      "parts-drive-through",
      "parts-shop",
      "parts-counter",
      "parts-tire-display",
      "parts-merchandise"
    ]
  }
];

const QUESTION_TO_SECTION = SECTION_CONFIG.reduce((acc, section) => {
  section.questionKeys.forEach((key) => {
    acc[key] = section.key;
  });
  return acc;
}, {});

const EMPTY_COUNTS = { yes: 0, no: 0, na: 0, total: 0 };

const initSectionCounts = () => {
  return SECTION_CONFIG.reduce((acc, section) => {
    acc[section.key] = { ...EMPTY_COUNTS };
    return acc;
  }, {});
};

const normalizeAnswer = (value) => String(value || "").trim().toLowerCase();

const toPercent = (value) => {
  if (value === null || Number.isNaN(value)) return "-";
  return `${Math.round(value)}%`;
};

const heatmapColor = (percent) => {
  if (percent === null || Number.isNaN(percent)) return "#f2f2f2";
  const clamped = Math.max(0, Math.min(100, percent));
  const hue = (clamped / 100) * 120;
  return `hsl(${hue}, 70%, 85%)`;
};

const SiteVisitExecutiveReport = () => {
  const [submissions, setSubmissions] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setStatus("loading");
        const response = await axios.get(Links.getSiteVisitChecklists, {
          params: { company: "FFUN", limit: 1000, offset: 0 }
        });
        const payload = response?.data || {};
        const items = payload.data || payload.rows || payload.results || payload;
        setSubmissions(Array.isArray(items) ? items : []);
        setStatus("ready");
      } catch (err) {
        console.error("Site visit executive report fetch error:", err);
        setError("Unable to load submissions. Please try again later.");
        setStatus("error");
      }
    };

    fetchData();
  }, []);

  const reportRows = useMemo(() => {
    const storeMap = new Map();

    submissions.forEach((submission) => {
      const storeName =
        submission?.storeName || submission?.store_name || submission?.store || "Unknown";
      const storeId = submission?.storeId ?? submission?.store_id ?? storeName;
      const storeKey = String(storeId);

      if (!storeMap.has(storeKey)) {
        storeMap.set(storeKey, {
          storeId,
          storeName,
          totalSubmissions: 0,
          counts: { ...EMPTY_COUNTS },
          sectionCounts: initSectionCounts(),
          lastSubmittedAt: null
        });
      }

      const storeRecord = storeMap.get(storeKey);
      storeRecord.totalSubmissions += 1;

      const submittedAt = submission?.submittedAt || submission?.submitted_at;
      if (submittedAt) {
        const current = storeRecord.lastSubmittedAt;
        if (!current || moment(submittedAt).isAfter(moment(current))) {
          storeRecord.lastSubmittedAt = submittedAt;
        }
      }

      const answers = submission?.answers || {};
      Object.entries(answers).forEach(([questionKey, answerValue]) => {
        const sectionKey = QUESTION_TO_SECTION[questionKey];
        if (!sectionKey) return;

        const normalized = normalizeAnswer(answerValue);
        if (normalized !== "yes" && normalized !== "no" && normalized !== "na") return;

        storeRecord.counts[normalized] += 1;
        storeRecord.counts.total += 1;

        const section = storeRecord.sectionCounts[sectionKey];
        section[normalized] += 1;
        section.total += 1;
      });
    });

    return Array.from(storeMap.values()).map((store) => {
      const scoredTotal = store.counts.yes + store.counts.no;
      const score = scoredTotal ? (store.counts.yes / scoredTotal) * 100 : null;
      const sectionScores = SECTION_CONFIG.reduce((acc, section) => {
        const stats = store.sectionCounts[section.key];
        const sectionTotal = stats.yes + stats.no + stats.na;
        acc[section.key] = sectionTotal ? (stats.yes / sectionTotal) * 100 : null;
        return acc;
      }, {});

      return {
        ...store,
        score,
        sectionScores
      };
    });
  }, [submissions]);

  const kpis = useMemo(() => {
    const totals = reportRows.reduce(
      (acc, store) => {
        acc.submissions += store.totalSubmissions;
        acc.yes += store.counts.yes;
        acc.no += store.counts.no;
        acc.na += store.counts.na;
        return acc;
      },
      { submissions: 0, yes: 0, no: 0, na: 0 }
    );

    const scoredTotal = totals.yes + totals.no;
    const score = scoredTotal ? (totals.yes / scoredTotal) * 100 : null;

    return {
      submissions: totals.submissions,
      yes: totals.yes,
      no: totals.no,
      na: totals.na,
      score
    };
  }, [reportRows]);

  return (
    <div className="svr-page">
      <div className="svr-hero">
        <div>
          <p className="svr-eyebrow">Executive Snapshot</p>
          <h1>Site Visit Executive Report</h1>
          <p className="svr-subtitle">
            Heatmap view of checklist performance by store and section.
          </p>
        </div>
        <div className="svr-pill">All Stores</div>
      </div>

      <div className="svr-kpis">
        <div className="svr-card">
          <p className="svr-card-label">Avg Score</p>
          <h3>{toPercent(kpis.score)}</h3>
          <span className="svr-card-meta">Yes / (Yes + No)</span>
        </div>
        <div className="svr-card">
          <p className="svr-card-label">Submissions</p>
          <h3>{kpis.submissions}</h3>
          <span className="svr-card-meta">Total checklists</span>
        </div>
        <div className="svr-card">
          <p className="svr-card-label">Yes</p>
          <h3>{kpis.yes}</h3>
          <span className="svr-card-meta">Positive answers</span>
        </div>
        <div className="svr-card">
          <p className="svr-card-label">No</p>
          <h3>{kpis.no}</h3>
          <span className="svr-card-meta">Needs attention</span>
        </div>
        <div className="svr-card">
          <p className="svr-card-label">N/A</p>
          <h3>{kpis.na}</h3>
          <span className="svr-card-meta">Not applicable</span>
        </div>
      </div>

      <div className="svr-section">
        <div className="svr-section-header">
          <h2>Store Performance Heatmap</h2>
          <div className="svr-legend">
            <span>Low</span>
            <div className="svr-legend-bar" />
            <span>High</span>
          </div>
        </div>

        {status === "loading" && <div className="svr-empty">Loading report...</div>}
        {status === "error" && <div className="svr-empty">{error}</div>}
        {status === "ready" && reportRows.length === 0 && (
          <div className="svr-empty">No submissions available yet.</div>
        )}

        {status === "ready" && reportRows.length > 0 && (
          <div className="svr-table-wrap">
            <table className="svr-table">
              <thead>
                <tr>
                  <th>Store</th>
                  {SECTION_CONFIG.map((section) => (
                    <th key={section.key}>{section.label}</th>
                  ))}
                  <th>Score</th>
                  <th>Submissions</th>
                  <th>Last Submitted</th>
                </tr>
              </thead>
              <tbody>
                {reportRows
                  .slice()
                  .sort((a, b) => (b.score || 0) - (a.score || 0))
                  .map((store) => (
                    <tr key={store.storeId}>
                      <td className="svr-store">{store.storeName}</td>
                      {SECTION_CONFIG.map((section) => {
                        const percent = store.sectionScores[section.key];
                        return (
                          <td
                            key={`${store.storeId}-${section.key}`}
                            style={{ backgroundColor: heatmapColor(percent) }}
                            className="svr-heat-cell"
                          >
                            {toPercent(percent)}
                          </td>
                        );
                      })}
                      <td className="svr-score">{toPercent(store.score)}</td>
                      <td>{store.totalSubmissions}</td>
                      <td>
                        {store.lastSubmittedAt
                          ? moment(store.lastSubmittedAt).format("MMM D, YYYY")
                          : "-"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteVisitExecutiveReport;
